import * as p from '@clack/prompts'
import type { GeneratedSchemaField, GeneratedSchemaKind } from '@cli-types/core-engine/add-field.js'
import type { PromptFieldOptions, SchemaPromptContext } from '@cli-types/next-adapter/commands.js'
import { NON_PLACEHOLDER_FIELD_TYPES } from '@cli-constants/schema-codegen.js'
import { promptConfirm } from './prompt-confirm.js'
import { promptOptionalText } from './prompt-optional-text.js'
import { promptSelectValue } from './prompt-select-value.js'

export async function applyAdvancedOptions(
  context: SchemaPromptContext,
  field: GeneratedSchemaField,
  kind: GeneratedSchemaKind,
  type: string,
  options: PromptFieldOptions
): Promise<void> {
  if (options.promptAdvancedOptions === false) {
    return
  }

  const configure = await promptConfirm(context, 'Configure advanced options?', false)
  if (!configure) {
    return
  }

  const record = field as unknown as Record<string, unknown>
  const omitLabel = await promptConfirm(context, 'Omit the label property?', false)
  if (omitLabel) {
    record.label = undefined
  }

  if (!NON_PLACEHOLDER_FIELD_TYPES.has(type) && !options.derivePlaceholder) {
    const placeholder = await promptOptionalText(context, 'Placeholder')
    if (placeholder) {
      record.placeholder = placeholder
    }
  }

  if (options.promptHint !== false) {
    const hint = await promptOptionalText(context, 'Hint')
    if (hint) {
      record.hint = hint
    }
  }

  if (
    options.promptHeight !== false &&
    ['text', 'textarea', 'markdown', 'richtext'].includes(type)
  ) {
    const height = await promptSelectValue(context, 'Height', [
      { value: 'auto', label: 'auto' },
      { value: 'fill', label: 'fill' }
    ] as const)
    if (height !== 'auto') {
      record.height = height
    }
  }

  if (kind !== 'form' && ['string', 'varchar', 'icon'].includes(type)) {
    const length = await promptOptionalText(context, 'Max length')
    if (length) {
      const parsed = Number(length)
      if (Number.isInteger(parsed) && parsed > 0) {
        record.length = parsed
      } else {
        p.log.warn('Skipped invalid length.')
      }
    }
  }

  if (kind !== 'form' && type === 'decimal') {
    const precision = await promptOptionalText(context, 'Precision')
    const scale = await promptOptionalText(context, 'Scale')
    const parsedPrecision = precision ? Number(precision) : undefined
    const parsedScale = scale ? Number(scale) : undefined
    if (
      parsedPrecision !== undefined &&
      parsedScale !== undefined &&
      Number.isInteger(parsedPrecision) &&
      Number.isInteger(parsedScale)
    ) {
      record.precision = parsedPrecision
      record.scale = parsedScale
    }
  }
}
